import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ChatStorage, ChatMessage, ChatSession, GroupedSessions } from '../../../interfaces/chat.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { STORAGE_CHAT_HISTORY_SUFFIX, STORAGE_CHAT_MESSAGES_SUFFIX } from '../../constants/craig.constants';
import { AuthService } from '../auth/auth.service';
import { UtilsService } from '../utils/utils.service';

@Injectable({
    providedIn: 'root'
})
export class ChatStorageService {
    private readonly sessionsListChanged$: Subject<boolean> = new Subject<boolean>();
    private readonly chatStorage: ChatStorage = {};
    private sessionId: number = -1;

    constructor(
        private readonly localStorageService: LocalStorageService,
        private readonly authService: AuthService,
        private readonly utilsService: UtilsService
    ) {}

    addNewChat(sessionId?: number): number {
        const userId: string = this.authService.getUserEmail();

        this.sessionId = sessionId || Date.now();
        this.chatStorage[this.sessionId] = [...this.getStoredChatMessages(userId, this.sessionId)];

        return this.sessionId;
    }

    addMessageToChatHistory(message: ChatMessage, sessionId: number = this.sessionId): void {
        this.chatStorage[sessionId].push(message);

        this.saveMessagesToLocalStorage(this.chatStorage[sessionId], sessionId);
    }

    getCurrentChatId(): number {
        return this.sessionId;
    }

    getCurrentChatMessages(): ChatMessage[] {
        return this.chatStorage[this.sessionId];
    }

    getChatMessagesById(sessionId: number): ChatMessage[] {
        return this.chatStorage[sessionId];
    }

    getGroupedChatSessions(): GroupedSessions[] {
        const userId: string = this.authService.getUserEmail();

        return this.groupSessionsByMonth(this.getStoredSessions(userId));
    }

    changeSessionsList(): void {
        this.sessionsListChanged$.next(true);
    }

    chatSessionsListChange(): Observable<boolean> {
        return this.sessionsListChanged$.asObservable();
    }

    private getStoredSessions(userId: string): ChatSession[] {
        return this.localStorageService.getParsedItem<ChatSession[]>(`${userId}_${STORAGE_CHAT_HISTORY_SUFFIX}`) || [];
    }

    private setStoredSessions(userId: string, sessions: ChatSession[]): void {
        this.localStorageService.setParsedItem<ChatSession[]>(`${userId}_${STORAGE_CHAT_HISTORY_SUFFIX}`, sessions);
    }

    private getStoredChatMessages(userId: string, sessionId: number): ChatMessage[] {
        return (
            this.localStorageService.getParsedItem<ChatMessage[]>(
                `${userId}_${sessionId}_${STORAGE_CHAT_MESSAGES_SUFFIX}`
            ) || []
        );
    }

    private setStoredChatMessages(userId: string, sessionId: number, messages: ChatMessage[]): void {
        this.localStorageService.setParsedItem<ChatMessage[]>(
            `${userId}_${sessionId}_${STORAGE_CHAT_MESSAGES_SUFFIX}`,
            messages
        );
    }

    private saveMessagesToLocalStorage(messages: ChatMessage[], sessionId: number, tryCount: number = 0): void {
        const userId: string = this.authService.getUserEmail();

        try {
            this.saveSessionToLocalStorage(messages.length ? messages[0].content : '', userId, sessionId);
            this.setStoredChatMessages(userId, sessionId, messages);
        } catch (error: any) {
            if (this.localStorageService.isStorageExceededError(error)) {
                this.utilsService.cleanChatSessionStorage();
                this.changeSessionsList();

                if (tryCount === 0) {
                    this.saveMessagesToLocalStorage(messages, sessionId, tryCount + 1);
                }
            }
        }
    }

    private saveSessionToLocalStorage(title: string, userId: string, sessionId: number): void {
        const sessions: ChatSession[] = this.getStoredSessions(userId);

        if (!sessions.length || sessions.findIndex(({ id }: ChatSession) => id === sessionId) === -1) {
            const createDate: string = new Date(sessionId).toISOString();

            sessions.push({ id: sessionId, title, createDate });

            this.setStoredSessions(userId, sessions);
            this.changeSessionsList();
        }
    }

    private groupSessionsByMonth(sessions: ChatSession[]): GroupedSessions[] {
        const reversedSessions: ChatSession[] = [...sessions].reverse();
        const map: Map<string, ChatSession[]> = new Map();

        for (const session of reversedSessions) {
            const key: string = new Date(session.createDate).toLocaleString('en-US', {
                month: 'long',
                year: 'numeric'
            });

            if (!map.has(key)) {
                map.set(key, []);
            }

            map.get(key)!.push(session);
        }

        return Array.from(map.entries()).map(([monthYear, sessions]: [string, ChatSession[]]) => ({
            monthYear,
            sessions
        }));
    }
}
